import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToSection } from "@/lib/lenis";

const PRODUCT_IMG = "/images/inspect.jpg";

const specs = [
  { label: "GRADE", value: "40–42% Cr2O3", note: "Consistently assayed — higher grades processed on request", wide: true, testid: "spec-grade" },
  { label: "FORMAT", value: "Washed Concentrate", note: "Processed at our efficient wash plant", wide: false, testid: "spec-format" },
  { label: "APPLICATION", value: "Stainless Steel", note: "Ferrochrome feedstock for global mills", wide: false, testid: "spec-application" },
  { label: "THROUGHPUT", value: "+15,000 t/mo", note: "Feedstock processing, scaling steadily", wide: false, testid: "spec-throughput" },
  { label: "REACH", value: "Worldwide", note: "Rail, road and port corridor logistics", wide: false, testid: "spec-reach" },
];

export const Product = () => (
  <section id="product" data-testid="product-section" className="bg-surface px-6 md:px-12 lg:px-24 py-24 md:py-32">
    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5 relative">
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-chrome mb-6">The Product</p>
        <h2 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-ink" data-testid="product-heading">
          CHROME<br />40–42%<br /><span className="text-chrome">& BEYOND.</span>
        </h2>
        <p className="mt-8 text-base md:text-lg leading-relaxed text-slate-600 max-w-md">
          High-grade chrome concentrate engineered for stainless steel production —
          from our flagship 40–42% grade to higher grades, sourced, washed and delivered to specification.
        </p>
        <button
          data-testid="product-enquiry-button"
          onClick={() => scrollToSection("#contact")}
          className="mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-chrome border-b border-chrome pb-1 hover:gap-4 transition-[gap] duration-300"
        >
          Request a Specification Sheet <ArrowUpRight size={14} />
        </button>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 relative"
        >
          <div className="absolute -bottom-4 -left-4 w-full h-full bg-chrome/10 pointer-events-none" />
          <img
            src={PRODUCT_IMG}
            alt="Chrome concentrate stockpile inspection on site"
            data-testid="product-image"
            className="spotlight relative w-full aspect-[4/3] object-cover"
          />
        </motion.div>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10 self-start lg:mt-24">
        {specs.map((s, i) => (
          <motion.div
            key={s.label}
            data-testid={s.testid}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.08 }}
            className={`bg-paper p-8 md:p-10 hover:bg-white transition-colors duration-300 ${s.wide ? "sm:col-span-2" : ""}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-4">{s.label}</div>
            <div className="font-display font-semibold text-2xl md:text-3xl tracking-tight text-ink">{s.value}</div>
            <div className="text-sm text-slate-500 mt-2">{s.note}</div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
